import { Trans, useTranslations } from "adapters/i18n";
import React, { useMemo, useState } from "react";
import { ConfigurationFeature } from "../utils/configurations.utils";

import type {
  ConfigurationOptionFeature,
  FeaturesVisibility,
} from "../configurations.types";
type UseConfigurationFeaturesProps = {
  featuresVisibility: FeaturesVisibility;
};

export function useConfigurationFeatures({
  featuresVisibility,
}: UseConfigurationFeaturesProps) {
  const { t } = useTranslations();

  const [selectedFeature, setSelectedFeature] = useState<ConfigurationFeature>(
    ConfigurationFeature.ALL
  );
  const handleChangeFeature = (feature: ConfigurationFeature) => {
    setSelectedFeature(feature);
  };

  const items = useMemo<
    Record<ConfigurationFeature, ConfigurationOptionFeature>
  >(
    () => ({
      [ConfigurationFeature.ALL]: {
        id: ConfigurationFeature.ALL,
        label: t("common:all-fem"),
      },
      [ConfigurationFeature.ARROBA_BALANCE]: {
        id: ConfigurationFeature.ARROBA_BALANCE,
        label: t("arrobaBalance:arroba-balance"),
      },
      [ConfigurationFeature.BIRTH]: {
        id: ConfigurationFeature.BIRTH,
        label: t("movements:birth"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-birth-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.CONCILIATION]: {
        id: ConfigurationFeature.CONCILIATION,
        label: t("conciliation:conciliation"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-conciliation-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.CONSUMPTION_CLOSURE]: {
        id: ConfigurationFeature.CONSUMPTION_CLOSURE,
        label: t("common:menu.consumption-closure"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-consumption-closure-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.CREEP_FEEDING]: {
        id: ConfigurationFeature.CREEP_FEEDING,
        label: t("supplementation:creep-trough"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-creep-feeding-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.DEATH]: {
        id: ConfigurationFeature.DEATH,
        label: t("movements:death"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-death-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.DRUM]: {
        id: ConfigurationFeature.DRUM,
        label: t("formulation:drum"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-drum-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.ENTRIES_AND_EXITS]: {
        id: ConfigurationFeature.ENTRIES_AND_EXITS,
        label: t("movements:entry-and-exit"),
        modalDescription: (
          <Trans
            i18nKey="configurations:feature-entry-exit-disclaimer"
            components={{ ul: <ul />, li: <li /> }}
          />
        ),
      },
      [ConfigurationFeature.FARM_GOALS]: {
        id: ConfigurationFeature.FARM_GOALS,
        label: t("locales:farm-goals"),
        modalDescription: t("configurations:feature-farm-goal-disclaimer"),
      },
      [ConfigurationFeature.HERD_EVOLUTION_AND_NUTRITION_PLAN]: {
        id: ConfigurationFeature.HERD_EVOLUTION_AND_NUTRITION_PLAN,
        label: t("planning:herd-evolution-and-nutrition-plan"),
        modalDescription: t("configurations:feature-herd-evolution-disclaimer"),
      },
    }),
    [t]
  );

  const itemToVisibilityMap: Partial<Record<ConfigurationFeature, boolean>> = {
    [ConfigurationFeature.BIRTH]: featuresVisibility.showDeathsAndBirths,
    [ConfigurationFeature.CONSUMPTION_CLOSURE]:
      featuresVisibility.showConsumptionClosure,
    [ConfigurationFeature.CREEP_FEEDING]: featuresVisibility.showCreepFeeding,
    [ConfigurationFeature.DEATH]: featuresVisibility.showDeathsAndBirths,
    [ConfigurationFeature.DRUM]: featuresVisibility.showDrum,
    [ConfigurationFeature.HERD_EVOLUTION_AND_NUTRITION_PLAN]:
      featuresVisibility.showHerdEvolutionAndNutritionPlan,
  };

  const isItemVisible = (item: ConfigurationFeature) => {
    return itemToVisibilityMap[item] ?? true;
  };

  const selectedFeatureItem = useMemo(
    () => items[selectedFeature] ?? items[ConfigurationFeature.ALL],
    [items, selectedFeature]
  );

  const sortedOptionItems = Object.values(items)
    .sort((a, b) => {
      if (a.id === ConfigurationFeature.ALL) return -1;
      if (b.id === ConfigurationFeature.ALL) return 1;
      return a.label.localeCompare(b.label);
    })
    .filter((item) => isItemVisible(item.id));

  return {
    featureItems: sortedOptionItems,
    handleChangeFeature,
    selectedFeature,
    selectedFeatureItem,
  };
}
